import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Instagram as IgIcon, Heart, Loader2 } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { BotanicalDivider } from "./Botanical";
import { SERVICES, CONTACT } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
    "w-full border border-gold/30 bg-ivory/5 px-5 py-4 text-sm text-ivory placeholder:text-ivory/40 outline-none transition-[border-color,box-shadow,background-color] duration-500 ease-elegant focus:border-gold focus:bg-ivory/10 focus:shadow-[0_0_0_3px_rgba(201,162,39,0.18)]";

export const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", event_type: "", event_date: "", message: "" });
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/inquiries`, form);
            toast.success("Thank you — your inquiry is on its way. We'll be in touch very soon.");
            setForm({ name: "", email: "", event_type: "", event_date: "", message: "" });
        } catch (err) {
            toast.error("Something interrupted your note — please try again, or email us directly.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative overflow-hidden bg-emerald-deep py-28 lg:py-40">
            <div
                className="pointer-events-none absolute inset-0 opacity-[0.04]"
                style={{
                    backgroundImage: "radial-gradient(circle at 50% 50%, #C9A227 1px, transparent 1px)",
                    backgroundSize: "70px 70px",
                }}
            />
            <div className="relative mx-auto grid max-w-6xl gap-16 px-6 lg:grid-cols-[1fr_1.15fr] lg:gap-24 lg:px-10">
                <div>
                    <SectionHeading
                        dark
                        eyebrow="Begin Your Story"
                        title="Book a Consultation"
                        script="Let's create something beautiful together"
                        align="left"
                    />
                    <Reveal delay={0.3}>
                        <p className="mt-8 text-sm leading-loose text-ivory/70">
                            Share a few details about your celebration and we will respond within
                            one business day — with ideas, availability and a little inspiration
                            to begin with.
                        </p>
                    </Reveal>
                    <Reveal delay={0.4}>
                        <div className="mt-10 flex flex-col gap-6" data-testid="contact-details">
                            <a
                                href={`mailto:${CONTACT.email}`}
                                data-testid="contact-email-link"
                                className="group flex items-center gap-5"
                            >
                                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors duration-500 group-hover:bg-gold group-hover:text-emerald-deep">
                                    <Mail size={19} strokeWidth={1.5} />
                                </span>
                                <span>
                                    <span className="block text-[10px] uppercase tracking-[0.35em] text-gold/70">Email</span>
                                    <span className="text-sm text-ivory/85 transition-colors duration-300 group-hover:text-gold">{CONTACT.email}</span>
                                </span>
                            </a>
                            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="group flex items-center gap-5">
                                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors duration-500 group-hover:bg-gold group-hover:text-emerald-deep">
                                    <Phone size={19} strokeWidth={1.5} />
                                </span>
                                <span>
                                    <span className="block text-[10px] uppercase tracking-[0.35em] text-gold/70">Phone</span>
                                    <span className="text-sm text-ivory/85 transition-colors duration-300 group-hover:text-gold">{CONTACT.phone}</span>
                                </span>
                            </a>
                            <a
                                href={CONTACT.instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-instagram-link"
                                className="group flex items-center gap-5"
                            >
                                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/50 text-gold transition-colors duration-500 group-hover:bg-gold group-hover:text-emerald-deep">
                                    <IgIcon size={19} strokeWidth={1.5} />
                                </span>
                                <span>
                                    <span className="block text-[10px] uppercase tracking-[0.35em] text-gold/70">Instagram</span>
                                    <span className="text-sm text-ivory/85 transition-colors duration-300 group-hover:text-gold">{CONTACT.instagram}</span>
                                </span>
                            </a>
                        </div>
                    </Reveal>
                    <Reveal delay={0.5}>
                        <BotanicalDivider className="mt-12" width={240} />
                    </Reveal>
                </div>

                <Reveal delay={0.25}>
                    <form
                        onSubmit={submit}
                        data-testid="inquiry-form"
                        className="border border-gold/25 bg-emerald/60 p-8 backdrop-blur-sm sm:p-10"
                    >
                        <div className="grid gap-6 sm:grid-cols-2">
                            <div className="sm:col-span-1">
                                <label htmlFor="inquiry-name" className="mb-2 block text-[10px] uppercase tracking-[0.35em] text-gold/80">
                                    Your Name *
                                </label>
                                <input
                                    id="inquiry-name"
                                    data-testid="inquiry-name-input"
                                    required
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Amara Okafor"
                                    className={inputCls}
                                />
                            </div>
                            <div className="sm:col-span-1">
                                <label htmlFor="inquiry-email" className="mb-2 block text-[10px] uppercase tracking-[0.35em] text-gold/80">
                                    Email *
                                </label>
                                <input
                                    id="inquiry-email"
                                    data-testid="inquiry-email-input"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="you@example.com"
                                    className={inputCls}
                                />
                            </div>
                            <div>
                                <label htmlFor="inquiry-event-type" className="mb-2 block text-[10px] uppercase tracking-[0.35em] text-gold/80">
                                    Event Type *
                                </label>
                                <select
                                    id="inquiry-event-type"
                                    data-testid="inquiry-event-type-select"
                                    required
                                    value={form.event_type}
                                    onChange={set("event_type")}
                                    className={`${inputCls} appearance-none [&>option]:bg-emerald-deep`}
                                >
                                    <option value="" disabled>
                                        Choose a celebration
                                    </option>
                                    {SERVICES.map((s) => (
                                        <option key={s.title} value={s.title}>
                                            {s.title}
                                        </option>
                                    ))}
                                    <option value="Something Else">Something Else</option>
                                </select>
                            </div>
                            <div>
                                <label htmlFor="inquiry-date" className="mb-2 block text-[10px] uppercase tracking-[0.35em] text-gold/80">
                                    Event Date
                                </label>
                                <input
                                    id="inquiry-date"
                                    data-testid="inquiry-date-input"
                                    type="date"
                                    value={form.event_date}
                                    onChange={set("event_date")}
                                    className={inputCls}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label htmlFor="inquiry-message" className="mb-2 block text-[10px] uppercase tracking-[0.35em] text-gold/80">
                                    Tell Us Your Vision *
                                </label>
                                <textarea
                                    id="inquiry-message"
                                    data-testid="inquiry-message-input"
                                    required
                                    rows={5}
                                    value={form.message}
                                    onChange={set("message")}
                                    placeholder="The mood, the venue, the colours you dream in…"
                                    className={`${inputCls} resize-none`}
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            data-testid="inquiry-submit-btn"
                            disabled={sending}
                            className="btn-sweep mt-8 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-deep transition-[transform,box-shadow,opacity] duration-500 ease-elegant hover:scale-[1.02] hover:shadow-[0_16px_50px_-10px_rgba(201,162,39,0.6)] disabled:cursor-wait disabled:opacity-70"
                        >
                            {sending ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" /> Sending…
                                </>
                            ) : (
                                <>
                                    Send Your Inquiry <Heart size={14} strokeWidth={1.6} />
                                </>
                            )}
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
